import {
  Building,
  Building2,
  ChartColumnBig,
  FileInput,
  FileSearch,
  House,
  Megaphone,
  Settings,
  Siren,
  SparkleIcon,
  Telescope,
} from 'lucide-react'
import { Suspense, lazy, useMemo } from 'react'
import { Link, Outlet } from 'react-router-dom'

import { usePrefetchAssessmentScoreLabels } from '@/api/assessments.hook'

import { SECTION_LABELS } from '@/const/label'

import { APP_BANNER_HEIGHT_PX, useAppBanners } from '@/lib/app/app-banners'
import { useDiscoveryModuleAccess } from '@/lib/app/discovery-module-access.hook'
import { useOrgDetails } from '@/lib/auth/orgid.hook'
import { useFeatureFlagEnabled } from '@/lib/config/featureflag'
import { AppRoute } from '@/lib/url/urls'

import { UserProfileButton } from '@/components/shared/user-profile-button/user-profile-button'
import { WebsiteLogo } from '@/components/shared/website-logo/website-logo'
import { CollapsibleSidebar } from '@/components/sidebar/collapsible-sidebar'
import { SIDEBAR_ITEMS } from '@/components/sidebar/consts'
import { SidebarTenantSwitcher } from '@/components/sidebar/sidebar-tenant-switcher'
import type { SidebarEntry, SidebarNavItemFields } from '@/components/sidebar/sidebar.types'

const AlertDrawer = lazy(() =>
  import('@/pages/drawers/alert-drawer').then((module) => ({ default: module.AlertDrawer })),
)
const SignalDrawer = lazy(() =>
  import('@/pages/drawers/signal-drawer').then((module) => ({ default: module.SignalDrawer })),
)
const ControlDrawer = lazy(() =>
  import('@/pages/drawers/control-drawer').then((module) => ({ default: module.ControlDrawer })),
)
const EngagementDrawer = lazy(() =>
  import('@/pages/drawers/engagement-drawer').then((module) => ({
    default: module.EngagementDrawer,
  })),
)
const ThirdPartyActivityLogDrawer = lazy(() =>
  import('@/pages/drawers/third-party-activity-log-drawer').then((module) => ({
    default: module.ThirdPartyActivityLogDrawer,
  })),
)
const QuestionnaireDrawer = lazy(() =>
  import('@/pages/drawers/questionnaire-drawer').then((module) => ({
    default: module.QuestionnaireDrawer,
  })),
)
const RequestDrawerWrapper = lazy(() =>
  import('@/pages/requests-page/components/request-drawer-wrapper').then((module) => ({
    default: module.RequestDrawerWrapper,
  })),
)
const FourthPartyDrawer = lazy(() =>
  import('@/pages/drawers/fourth-party-drawer').then((module) => ({
    default: module.FourthPartyDrawer,
  })),
)

const suspensify = (page: React.ReactNode) => <Suspense fallback={<></>}>{page}</Suspense>

function useSidebarNavigation() {
  const { isEnabled: fourthPartiesEnabled } = useFeatureFlagEnabled('fourth-parties')
  const { hasAccess: discoveryVisible } = useDiscoveryModuleAccess()
  const { isEnabled: aiGovernanceEnabled } = useFeatureFlagEnabled('ai-governance')
  const { isEnabled: outreachEnabled } = useFeatureFlagEnabled('outreach-campaigns')

  const entries = useMemo<SidebarEntry[]>(() => {
    const items: SidebarEntry[] = [
      {
        type: 'item',
        icon: <House className='size-4' />,
        label: 'Overview',
        href: AppRoute.OVERVIEW,
        extraTrackingProps: { item: SIDEBAR_ITEMS.OVERVIEW },
      },
      { type: 'section' },
    ]

    items.push({
      type: 'item',
      icon: <FileSearch className='size-4' />,
      label: SECTION_LABELS.assessments,
      href: AppRoute.ASSESSMENTS,
      extraTrackingProps: { item: SIDEBAR_ITEMS.ASSESSMENTS },
    })

    items.push({
      type: 'item',
      icon: <FileInput className='size-4' />,
      label: SECTION_LABELS.requests,
      href: AppRoute.PROCUREMENT_REQUESTS,
      extraTrackingProps: { item: SIDEBAR_ITEMS.REQUESTS },
    })

    items.push({ type: 'section' })

    items.push({
      type: 'item',
      icon: <Building className='size-4' />,
      label: SECTION_LABELS.thirdParties,
      href: AppRoute.THIRD_PARTIES,
      extraTrackingProps: { item: SIDEBAR_ITEMS.THIRD_PARTIES },
    })

    if (fourthPartiesEnabled) {
      items.push({
        type: 'item',
        icon: <Building2 className='size-4' />,
        label: 'Fourth-parties',
        href: AppRoute.FOURTH_PARTIES,
        extraTrackingProps: { item: SIDEBAR_ITEMS.FOURTH_PARTIES },
      })
    }

    items.push({ type: 'section' })

    if (discoveryVisible) {
      items.push({
        type: 'item',
        icon: <Telescope className='size-4' />,
        label: 'Discovery',
        href: AppRoute.DISCOVERED,
        extraTrackingProps: { item: SIDEBAR_ITEMS.DISCOVERY },
      })
    }

    items.push({
      type: 'item',
      icon: <Siren className='size-4' />,
      label: 'Alerts',
      href: AppRoute.MONITORING,
      extraTrackingProps: { item: SIDEBAR_ITEMS.MONITORING },
    })

    if (outreachEnabled) {
      items.push({
        type: 'item',
        icon: <Megaphone className='size-4' />,
        label: SECTION_LABELS.outreach,
        href: AppRoute.OUTREACH,
        extraTrackingProps: { item: SIDEBAR_ITEMS.OUTREACH },
      })
    }

    items.push({ type: 'section' })

    if (aiGovernanceEnabled) {
      items.push({
        type: 'item',
        icon: <SparkleIcon className='size-4' />,
        label: 'AI governance',
        href: AppRoute.AI_GOVERNANCE,
        extraTrackingProps: { item: SIDEBAR_ITEMS.AI_GOVERNANCE },
      })
    }

    items.push({
      type: 'item',
      icon: <ChartColumnBig className='size-4' />,
      label: SECTION_LABELS.dashboards,
      href: AppRoute.DASHBOARDS,
      extraTrackingProps: { item: SIDEBAR_ITEMS.DASHBOARDS },
    })

    return items
  }, [aiGovernanceEnabled, discoveryVisible, fourthPartiesEnabled, outreachEnabled])

  const bottomItems = useMemo<SidebarNavItemFields[]>(
    () => [
      {
        icon: <Settings className='size-4' />,
        label: 'Settings',
        href: AppRoute.SETTINGS,
        trackingEvent: 'sidebar_item_clicked',
        extraTrackingProps: { item: SIDEBAR_ITEMS.SETTINGS },
      },
    ],
    [],
  )

  return { entries, bottomItems }
}

export const AuthenticatedLayout = () => {
  const { orgDetails } = useOrgDetails()
  const { entries, bottomItems } = useSidebarNavigation()
  const banners = useAppBanners()
  usePrefetchAssessmentScoreLabels()

  return (
    <div
      className='flex h-screen w-full'
      style={{ paddingBottom: banners.length * APP_BANNER_HEIGHT_PX }}
    >
      <CollapsibleSidebar
        logo={
          <Link to={AppRoute.OVERVIEW}>
            <WebsiteLogo orgId={orgDetails?.orgId} variant='full' />
          </Link>
        }
        compactLogo={
          <Link to={AppRoute.OVERVIEW}>
            <WebsiteLogo orgId={orgDetails?.orgId} variant='icon' />
          </Link>
        }
        entries={entries}
        bottomItems={bottomItems}
        tenantSwitcher={({ expanded }) => <SidebarTenantSwitcher expanded={expanded} />}
        userBlock={({ expanded }) => <UserProfileButton showNameInTrigger={expanded} />}
      />
      <main className='flex min-w-0 flex-1 flex-col overflow-auto'>
        <Outlet />
      </main>
      {suspensify(<AlertDrawer />)}
      {suspensify(<SignalDrawer />)}
      {suspensify(<ControlDrawer />)}
      {suspensify(<EngagementDrawer />)}
      {suspensify(<ThirdPartyActivityLogDrawer />)}
      {suspensify(<QuestionnaireDrawer />)}
      {suspensify(<RequestDrawerWrapper />)}
      {suspensify(<FourthPartyDrawer />)}
    </div>
  )
}
